import React, { useEffect, useState } from "react";

import { addHours, endOfDay, set, startOfDay } from "date-fns";

import { RepeatFrequency } from "@hubcapp/types";
import { createFakeEvent } from "@ds-developer/frontend";
import { fireOnce } from "@ds-developer/frontend";
import { useTealium } from "@hubcapp/context";

import { UnavailableFormFieldsStateProviderProps } from "../../types";
import UnavailableFormFields from "./UnavailableFormFields";

const StateProvider: React.FC<UnavailableFormFieldsStateProviderProps> = ({
  handleChange,
  values,
  errors,
  touched,
}) => {
  const [allDay, setAllDay] = useState(false);
  const { trackEvent } = useTealium();
  const [startDate, setStartDate] = useState<Date | null | undefined>(
    values.startDateTime || null,
  );
  const [startTime, setStartTime] = useState<Date | null | undefined>(
    values.startDateTime || null,
  );
  const [endDate, setEndDate] = useState<Date | null | undefined>(
    values.endDateTime || null,
  );
  const [endTime, setEndTime] = useState<Date | null | undefined>(
    values.endDateTime || null,
  );

  const updateStartEndDates = () => {
    // set the end date to midnight of the start date if "all day" is checked
    if (allDay) {
      if (startDate) {
        handleChange(
          createFakeEvent(
            "startDateTime",
            set(startDate, {
              hours: 0,
              minutes: 0,
            }),
          ),
        );
      }

      if (endDate) {
        handleChange(
          createFakeEvent(
            "endDateTime",
            set(endDate, {
              hours: 23,
              minutes: 59,
            }),
          ),
        );
      }
    } else {
      if (startDate) {
        handleChange(
          createFakeEvent(
            "startDateTime",
            set(startDate, {
              hours: startTime ? startTime.getHours() : 0,
              minutes: startTime ? startTime.getMinutes() : 0,
            }),
          ),
        );
      }
      if (endDate) {
        handleChange(
          createFakeEvent(
            "endDateTime",
            set(endDate, {
              hours: endTime ? endTime.getHours() : 23,
              minutes: endTime ? endTime.getMinutes() : 59,
            }),
          ),
        );
      }
    }
  };

  const setStartAndEndDateToSameDate = () => {
    if (values.repeat) {
      setEndDate(startDate);
      handleChange(createFakeEvent("endDateTime", startDate));
    }
  };

  const handleAllDayToggle = () => {
    if (!startDate) {
      return;
    }

    if (allDay) {
      setStartTime(startOfDay(startDate));
      setEndTime(endOfDay(startDate));
      setEndDate(endOfDay(startDate));
      handleChange(createFakeEvent("endDate", startDate));
      handleChange(createFakeEvent("startDateTime", startOfDay(startDate)));
      handleChange(createFakeEvent("endDateTime", endOfDay(startDate)));
      fireOnce("Calendar|Interaction|Time Off - All Day", () => {
        trackEvent({
          eventCategory: "Calendar",
          eventAction: "Interaction",
          eventLabel: "Time Off - All Day",
        });
      });
    }

    handleChange(createFakeEvent("allDay", allDay));
  };

  const handleRepeatFocus = (value: RepeatFrequency | undefined) => {
    fireOnce(`Calendar|Interaction|Repeat - ${value}`, () => {
      if (value) {
        trackEvent({
          eventCategory: "Calendar",
          eventAction: "Interaction",
          eventLabel: `Repeat - ${value}`,
        });
      }
    });
  };

  const handleStartDateClick = () => {
    fireOnce("Calendar|Interaction|Time Off - Start Date", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Time Off - Start Date",
      });
    });
  };

  const handleEndDateClick = () => {
    fireOnce("Calendar|Interaction|Time Off - End Date", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Time Off - End Date",
      });
    });
  };

  const handleStartTimeClick = () => {
    fireOnce("Calendar|Interaction|Time Off - Start Time", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Time Off - Start Time",
      });
    });
  };

  const handleEndTimeClick = () => {
    fireOnce("Calendar|Interaction|Time Off - End Time", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Time Off - End Time",
      });
    });
  };

  const handleNotesClick = () => {
    fireOnce("Calendar|Interaction|Notes", () => {
      trackEvent({
        eventCategory: "Calendar",
        eventAction: "Interaction",
        eventLabel: "Notes",
      });
    });
  };

  useEffect(updateStartEndDates, [startDate, endDate, startTime, endTime]);
  useEffect(handleAllDayToggle, [allDay]);
  useEffect(setStartAndEndDateToSameDate, [values.repeat]);

  return (
    <UnavailableFormFields
      allDay={allDay}
      endDate={endDate}
      endTime={endTime}
      startDate={startDate}
      startTime={startTime}
      values={values}
      handleAllDayChange={e => setAllDay(e.target.checked)}
      handleChange={handleChange}
      handleEndDateChange={val => setEndDate(val)}
      handleEndDateClick={handleEndDateClick}
      handleEndTimeChange={val => setEndTime(val)}
      handleEndTimeClick={handleEndTimeClick}
      handleNotesClick={handleNotesClick}
      handleRepeatFocus={handleRepeatFocus}
      handleStartDateChange={val => setStartDate(val)}
      handleStartDateClick={handleStartDateClick}
      handleStartTimeChange={val => {
        setStartTime(val);
        if (val) {
          setEndTime(addHours(val, 2));
        }
      }}
      handleStartTimeClick={handleStartTimeClick}
      errors={errors}
      touched={touched}
    />
  );
};

export default StateProvider;
